import type { MarketType } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard'
import { AssetClass, Exchange } from '@/types/generated/common/types'

/** Everything the side panel's saved views and Refine controls can set. */
export interface WizardFilterState {
  exchanges: Set<Exchange>
  types: Set<MarketType>
  assetClass: AssetClass | null
  quote: string | null
}

export type WizardViewId = 'all' | 'hyperliquid' | 'cme' | 'tokenized' | 'options'

export interface WizardView {
  id: WizardViewId
  filters: WizardFilterState
}

// Databento's market-data-only venues: CME futures and the US equity/option
// listings. Grouped as one view since none of them is tradeable on its own.
const CME_US_EXCHANGES: readonly Exchange[] = [
  Exchange.EXCHANGE_GLBX,
  Exchange.EXCHANGE_XNAS,
  Exchange.EXCHANGE_XNYS,
  Exchange.EXCHANGE_OPRA,
]

// "Tokenized stocks" = equities listed on a crypto venue, so the stock
// exchanges themselves (Databento venues, KRX) are left out of that view.
const NON_CRYPTO_EXCHANGES: ReadonlySet<Exchange> = new Set([
  ...CME_US_EXCHANGES,
  Exchange.EXCHANGE_XKRX,
])

const emptyFilters = (): WizardFilterState => ({
  exchanges: new Set(),
  types: new Set(),
  assetClass: null,
  quote: null,
})

interface BuildWizardViewsOptions {
  availableExchanges: readonly Exchange[]
  availableAssetClasses: readonly AssetClass[]
  exchangeLocked: boolean
  typeLocked: boolean
}

/** Fixed presets, each dropped when the catalog (or a caller's locked filter)
 *  makes it empty or contradictory. "All" always stays. */
export function buildWizardViews({
  availableExchanges,
  availableAssetClasses,
  exchangeLocked,
  typeLocked,
}: BuildWizardViewsOptions): WizardView[] {
  const available = new Set(availableExchanges)
  const views: WizardView[] = [{ id: 'all', filters: emptyFilters() }]

  if (!exchangeLocked && available.has(Exchange.EXCHANGE_HYPERLIQUID)) {
    views.push({
      id: 'hyperliquid',
      filters: { ...emptyFilters(), exchanges: new Set([Exchange.EXCHANGE_HYPERLIQUID]) },
    })
  }

  const cmeExchanges = CME_US_EXCHANGES.filter((exchange) => available.has(exchange))
  if (!exchangeLocked && cmeExchanges.length > 0) {
    views.push({ id: 'cme', filters: { ...emptyFilters(), exchanges: new Set(cmeExchanges) } })
  }

  const cryptoExchanges = availableExchanges.filter(
    (exchange) => !NON_CRYPTO_EXCHANGES.has(exchange)
  )
  if (
    !exchangeLocked &&
    cryptoExchanges.length > 0 &&
    availableAssetClasses.includes(AssetClass.ASSET_CLASS_EQUITY)
  ) {
    views.push({
      id: 'tokenized',
      filters: {
        ...emptyFilters(),
        exchanges: new Set(cryptoExchanges),
        assetClass: AssetClass.ASSET_CLASS_EQUITY,
      },
    })
  }

  if (!typeLocked) {
    views.push({ id: 'options', filters: { ...emptyFilters(), types: new Set(['options']) } })
  }

  return views
}

const sameSet = <T>(left: Set<T>, right: Set<T>) =>
  left.size === right.size && [...left].every((value) => right.has(value))

/** The view whose filters exactly match the current ones — none once the user
 *  refines past a preset, so the check mark never claims a view that's no
 *  longer what's on screen. */
export function matchWizardView(
  views: readonly WizardView[],
  current: WizardFilterState
): WizardViewId | null {
  const match = views.find(
    ({ filters }) =>
      filters.assetClass === current.assetClass &&
      filters.quote === current.quote &&
      sameSet(filters.exchanges, current.exchanges) &&
      sameSet(filters.types, current.types)
  )
  return match?.id ?? null
}
